import {
    getGetSitesByParcelIdUrl,
    getGetSitesUrl
} from 'generatedTypes/client/services/site-controller';
import { useState } from 'react';

import useFetch from 'hooks/useFetch';
import usePagination from 'hooks/usePagination';
import useRetry from 'hooks/useRetry';

import useTranslation from './hooks/useTranslation';

import { DEFAULT_PAGE_SIZE } from './constants';
import type { FetchFields, FetchFieldsParams, SiteResponse } from './types';

const useFields = () => {
    const [isLoading, setIsLoading] = useState(false);
    const { authenticatedGet } = useFetch();
    const { withRetry } = useRetry();
    const { CONNECTION_ERROR } = useTranslation();

    const fetchFields: FetchFields = async request => {
        const { farmId, name, pageSize, pageToken, sort } = request;

        try {
            setIsLoading(true);

            const url = farmId
                ? getGetSitesByParcelIdUrl(farmId, { name, pageSize, pageToken, sort })
                : getGetSitesUrl({ pageSize, pageToken, sort });

            return await withRetry(
                () => authenticatedGet(url),
                CONNECTION_ERROR,
                () => fetchFields(request)
            );
        } finally {
            setIsLoading(false);
        }
    };

    const {
        data: fieldsData,
        fetch: paginatedFetch,
        reset,
        totalCount
    } = usePagination<SiteResponse>({
        callback: fetchFields
    });

    const handleFetch = (args?: FetchFieldsParams) => {
        const { farmId, name, pageSize = DEFAULT_PAGE_SIZE } = args || {};

        return paginatedFetch({
            farmId,
            name,
            pageSize
        });
    };

    return {
        fetch: handleFetch,
        fields: fieldsData as SiteResponse[],
        isLoading,
        reset,
        totalCount
    };
};

export default useFields;
